<template>
  <slot
    v-if="useDefaultOnly"
    :experiment="experiment"
  />
  <slot
    v-else
    :name="slot"
    :experiment="experiment"
    :slot-name="slot"
  />
</template>

<script setup lang="ts">
/**
 * How to use this component:
 *
 * - Make sure to check what type of experiment you are using in the experimentConfig
 * - Features use `enabled`/`disabled` for the slot names
 * - Experiments use the variant name for the slot name
 * - Configuration experiments use `configured` for the slot name
 * - `off` variant always renders the control slot
 *
 * Example feature:
 * <ExperimentComponent experiment-key="rvz12345ShowNewHomepage">
 *   <template #enabled>
 *     rvz12345ShowNewHomepage is enabled
 *   </template>
 *   <template #disabled>
 *     rvz12345ShowNewHomepage is disabled
 *   </template>
 * </ExperimentComponent>
 *
 * Example experiment:
 * <ExperimentComponent experiment-key="rvz12346AddonExperiment">
 *   <template #variant>
 *     rvz12346AddonExperiment is variant
 *   </template>
 *   <!-- Will also show for the `off` variant -->
 *   <template #control>
 *     rvz12346AddonExperiment is control
 *   </template>
 * </ExperimentComponent>
 *
 * Full reference: https://rvezy.atlassian.net/wiki/spaces/RVEZY/pages/2026995744/Experimentation+Feature+Flags
 */
import type { EXPERIMENT_KEYS } from '#shared/constants/experiment'

const props = defineProps<{
  experimentKey: EXPERIMENT_KEYS
  useDefaultOnly?: boolean
}>()

const { getExperiment, trackView } = useExperiment()
const experiment = computed(() => getExperiment(props.experimentKey))

onMounted(() => {
  trackView(props.experimentKey)
})

// Dynamically determine which slot to render based on experiment config
const slot = computed(() => {
  const currentExperiment = experiment.value
  if (!currentExperiment) return 'control'

  // For experiments with variants, use the current variant as slot name
  if (currentExperiment.type === 'Experiment') {
    const variant = currentExperiment.variant?.toString()?.toLowerCase()

    // When `off` is passed, we always render the control slot
    if (variant === 'off') {
      return 'control'
    }

    return variant
  }

  // For feature flags, use 'enabled' or 'disabled' based on whether the feature is enabled.
  // isFeatureEnabled is not usable here because the prop accepts any key, not just a FeatureKey.
  if (currentExperiment.type === 'Feature') {
    return currentExperiment.variant ? 'enabled' : 'disabled'
  }

  // For configuration experiments, use 'configured' as the slot name
  if (currentExperiment.type === 'Configuration') {
    return 'configured'
  }

  return undefined
})
</script>
